import {
	// eslint-disable-next-line no-restricted-imports
	createContext,
	ReactNode,
	useCallback,
	// eslint-disable-next-line no-restricted-imports
	useContext,
	useMemo,
	useState,
} from 'react';
import ErrorModal from 'components/ErrorModal/ErrorModal';
import APIError from 'types/api/error';

export interface ErrorModalOptions {
	/** 오류 뒤에도 호출처의 폼과 입력값이 남아 있을 때만 true — 거짓 안내 방지. */
	keepsInput?: boolean;
	/** 응답 없이 끊긴 요청을 그 자리에서 다시 보낼 수 있을 때 넘긴다. */
	onRetry?: () => void;
}

interface ErrorModalContextType {
	showErrorModal: (error: APIError, options?: ErrorModalOptions) => void;
	hideErrorModal: () => void;
	isErrorModalVisible: boolean;
}

const ErrorModalContext = createContext<ErrorModalContextType | undefined>(
	undefined,
);

export function ErrorModalProvider({
	children,
}: {
	children: ReactNode;
}): JSX.Element {
	const [error, setError] = useState<APIError | null>(null);
	const [options, setOptions] = useState<ErrorModalOptions>({});
	const [isVisible, setIsVisible] = useState(false);

	const showErrorModal = useCallback(
		(error: APIError, nextOptions: ErrorModalOptions = {}): void => {
			setError(error);
			setOptions(nextOptions);
			setIsVisible(true);
		},
		[],
	);

	const hideErrorModal = useCallback((): void => {
		setError(null);
		setOptions({});
		setIsVisible(false);
	}, []);

	const value = useMemo(
		() => ({ showErrorModal, hideErrorModal, isErrorModalVisible: isVisible }),
		[showErrorModal, hideErrorModal, isVisible],
	);

	return (
		<ErrorModalContext.Provider value={value}>
			{children}
			{isVisible && error && (
				<ErrorModal
					error={error}
					onClose={hideErrorModal}
					open={isVisible}
					keepsInput={options.keepsInput}
					onRetry={options.onRetry}
				/>
			)}
		</ErrorModalContext.Provider>
	);
}

export const useErrorModal = (): ErrorModalContextType => {
	const context = useContext(ErrorModalContext);
	if (!context) {
		throw new Error('useErrorModal must be used within an ErrorModalProvider');
	}
	return context;
};
